
import {customFetcher} from "./customFetcher";
import {IReservationPrice, reservationPriceSchema} from "../Interfaces/IReservationPrice";
import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";

const RESERVATION_PRICE_QUERY_KEY = ['reservationPrice'];

export const useGetReservationPrice = ({ includeDeleted = false }: { includeDeleted?: boolean } = {}) => {
    const { data: reservationPriceArray, isLoading: isLoadingReservationPriceArray } = useQuery<IReservationPrice[]>({
        refetchOnWindowFocus: false,
        queryKey: [...RESERVATION_PRICE_QUERY_KEY, includeDeleted],
        queryFn: async () => {
            const data = await customFetcher<unknown[]>({
                endpoint: `/reservations-price/${includeDeleted ? '?include_deleted=true' : ''}`,
            });
            return reservationPriceSchema.array().parse(data);
        },
        initialData: [],
    });
    return { reservationPriceArray: reservationPriceArray as IReservationPrice[], isLoadingReservationPriceArray: isLoadingReservationPriceArray };
};

export const useCreateReservationPrice = () => {
    const queryClient = useQueryClient();
    const { mutateAsync: createReservationPrice, isPending: isCreatingReservationPrice } = useMutation<
        IReservationPrice,
        Error,
        Omit<IReservationPrice, 'id'>
    >({
        mutationFn: (newPrice) =>
            customFetcher<IReservationPrice>({
                endpoint: '/reservations-price/',
                method: 'POST',
                body: newPrice,
            }),
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: RESERVATION_PRICE_QUERY_KEY });
        },
    });

    return { createReservationPrice, isCreatingReservationPrice };
};

export const useUpdateReservationPrice = () => {
    const queryClient = useQueryClient();
    const { mutateAsync: updateReservationPrice, isPending: isUpdatingReservationPrice} = useMutation<
        IReservationPrice,
        Error,
        IReservationPrice
    >({
        mutationFn: (updatedPrice: IReservationPrice) =>
            customFetcher<IReservationPrice>({
                endpoint: `/reservations-price/${updatedPrice.id}/`,
                method: 'PUT',
                body: updatedPrice,
            }),
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: RESERVATION_PRICE_QUERY_KEY });
        },
    });

    return { updateReservationPrice: updateReservationPrice, isUpdatingReservationPrice: isUpdatingReservationPrice};
};

export const useDeleteReservationPrice = () => {
    const queryClient = useQueryClient();
    const { mutateAsync: deleteReservationPrice, isPending: isDeletingReservationPrice } = useMutation<void, Error, number>({
        mutationFn: async (id: number) => {
            await customFetcher({
                endpoint: `/reservations-price/${id}/`,
                method: 'DELETE',
            });
        },
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: RESERVATION_PRICE_QUERY_KEY });
        },
    });

    return { deleteReservationPrice, isDeletingReservationPrice };
};
